import { applyClinicInformationApiValidationErrors } from '@/pages/admin/site-admin-clinic-info/applyClinicInformationApiErrors';
import {
  AuthorizedReceiptFormData,
  ClinicCreateFormData,
  clinicCreateFormSchema,
  ClinicInfoFormData,
  ClinicPaymentInfoFormData,
  flattenClinicCreateData,
  OtherInfoFormData,
  ReceiptFeeTransferFormData,
} from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import { toDateStr } from '@/pages/admin/site-admin-clinic-info/clinicInfoPeriodUtils';
import type {
  AdjacentPeriodChecker,
  LatestAvailableToGetter,
} from '@components/organisms/ClinicInfoCreateForm';
import { AppBreadcrumb, AppButton, AppCard, AppContainer } from '@components/atoms';
import { ClinicInfoCreateForm } from '@components/organisms';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

type FlattenedClinicData = ReturnType<typeof flattenClinicCreateData>;

interface SiteAdminClinicInfoFormTemplateProps {
  breadcrumbItems: BreadcrumbItemType[];
  onSubmit?: (data: ClinicCreateFormData) => Promise<void> | void;
  isLoading?: boolean;
  isEdit?: boolean;
  isCopy?: boolean;
  clinicId?: string;
  clinicInfoData?: FlattenedClinicData;
  /** Site type (e.g. medix) for navigation */
  site?: string;
}

const SiteAdminClinicInfoFormTemplate = ({
  breadcrumbItems,
  onSubmit,
  isLoading = false,
  isEdit = false,
  isCopy = false,
  clinicId,
  clinicInfoData,
  site: siteProp,
}: SiteAdminClinicInfoFormTemplateProps) => {
  const navigate = useNavigate();
  const { site: siteFromParams, id } = useParams<{ site: string; id?: string }>();
  const currentSite = siteProp ?? siteFromParams ?? 'medix';
  const latestAvailableToGetterRef = useRef<LatestAvailableToGetter | null>(null);
  const adjacentPeriodCheckerRef = useRef<AdjacentPeriodChecker | null>(null);

  const methods = useTrimmedForm<ClinicCreateFormData>({
    resolver: zodResolver(clinicCreateFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      clinicInfo: {
        clinic_id: undefined,
        name: '',
        name_kana: '',
        available_from: null,
        available_to: null,
        open_week: [],
        open_time_from: null,
        open_time_to: null,
        postal_code: '',
        prefecture_id: undefined,
        address1: '',
        address2: '',
        address3: '',
        telephone: '',
        mobile: '',
        fax: '',
        email: '',
      },
      clinicPaymentInfo: {
        bank_payment_type_id: undefined,
        bank_code: '',
        bank_name: '',
        bank_name_kana: '',
        bank_type_id: undefined,
        bank_name_suffix: '',
        bank_branch_code: '',
        bank_branch_name: '',
        bank_branch_name_kana: '',
        bank_branch_type_id: undefined,
        branch_branch_name_suffix: '',
        bank_account_type_id: undefined,
        account_number: '',
        account_name: '',
        account_name_kana: '',
      },
      authorizedReceipt: {
        representative_name: '',
        representative_address: '',
        representative_telephone: '',
      },
      receiptFeeTransfer: {
        transfer_bank_code: '',
        transfer_bank_name: '',
        transfer_bank_name_kana: '',
        transfer_bank_branch_code: '',
        transfer_bank_branch_name: '',
        transfer_bank_branch_name_kana: '',
        transfer_bank_account_type_id: undefined,
        transfer_bank_account_number: '',
        transfer_bank_account_name: '',
        transfer_bank_account_name_kana: '',
        billing_company_code: '',
        customer_code: '',
        is_excluded_transfer: false,
        is_required_billing: false,
      },
      otherInfo: {
        company_name: '',
        clinic_rank: undefined,
        payment_type: '',
        claim_start_yearmonth: '',
        claim_end_yearmonth: '',
        monthly_fee: undefined,
        is_company: false,
        sales_staff_id: '',
        remarks: '',
      },
    },
  });

  useEffect(() => {
    if ((isEdit || isCopy) && clinicInfoData) {
      const info = clinicInfoData;
      methods.reset({
        clinicInfo: {
          clinic_id: info.clinic_id,
          name: info.name,
          name_kana: info.name_kana,
          available_from: info.available_from ? dayjs(info.available_from) : null,
          available_to: info.available_to ? dayjs(info.available_to) : null,
          open_week: info.open_week?.map(Number) ?? [],
          open_time_from: info.open_time_from ? dayjs(`2000-01-01 ${info.open_time_from}`) : null,
          open_time_to: info.open_time_to ? dayjs(`2000-01-01 ${info.open_time_to}`) : null,
          postal_code: info.postal_code.replace(/-/g, ''),
          prefecture_id: info.prefecture_id,
          address1: info.address1,
          address2: info.address2,
          address3: info.address3,
          telephone: info.telephone,
          mobile: info.mobile,
          fax: info.fax,
          email: info.email,
        } as ClinicInfoFormData,
        clinicPaymentInfo: {
          bank_payment_type_id: info.bank_payment_type_id,
          bank_code: info.bank_code ?? '',
          bank_name: info.bank_name,
          bank_name_kana: info.bank_name_kana ?? '',
          bank_type_id: info.bank_type_id,
          bank_name_suffix: info.bank_name_suffix,
          bank_branch_code: info.bank_branch_code ?? '',
          bank_branch_name: info.bank_branch_name,
          bank_branch_name_kana: info.bank_branch_name_kana ?? '',
          bank_branch_type_id: info.bank_branch_type_id,
          branch_branch_name_suffix: info.branch_branch_name_suffix,
          bank_account_type_id: info.bank_account_type_id,
          account_number: info.account_number ?? '',
          account_name: info.account_name,
          account_name_kana: info.account_name_kana ?? '',
        } as ClinicPaymentInfoFormData,
        authorizedReceipt: {
          representative_name: info.representative_name,
          representative_address: info.representative_address,
          representative_telephone: info.representative_telephone,
        } as AuthorizedReceiptFormData,
        receiptFeeTransfer: {
          transfer_bank_code: info.transfer_bank_code ?? '',
          transfer_bank_name: info.transfer_bank_name,
          transfer_bank_name_kana: info.transfer_bank_name_kana ?? '',
          transfer_bank_branch_code: info.transfer_bank_branch_code ?? '',
          transfer_bank_branch_name: info.transfer_bank_branch_name,
          transfer_bank_branch_name_kana: info.transfer_bank_branch_name_kana ?? '',
          transfer_bank_account_type_id: info.transfer_bank_account_type_id,
          transfer_bank_account_number: info.transfer_bank_account_number ?? '',
          transfer_bank_account_name: info.transfer_bank_account_name,
          transfer_bank_account_name_kana: info.transfer_bank_account_name_kana ?? '',
          billing_company_code: info.billing_company_code ?? '',
          customer_code: info.customer_code ?? '',
          is_excluded_transfer: info.is_excluded_transfer,
          is_required_billing: info.is_required_billing,
        } as ReceiptFeeTransferFormData,
        otherInfo: {
          company_name: info.company_name,
          clinic_rank: info.clinic_rank,
          payment_type: info.payment_type,
          claim_start_yearmonth: info.claim_start_yearmonth
            ? dayjs(String(info.claim_start_yearmonth), 'YYYYMM')
            : null,
          claim_end_yearmonth: info.claim_end_yearmonth
            ? dayjs(String(info.claim_end_yearmonth), 'YYYYMM')
            : null,
          monthly_fee: info.monthly_fee,
          is_company: info.is_company,
          sales_staff_id: info.sales_staff_id,
          remarks: info.remarks,
        } as OtherInfoFormData,
      });
    }
  }, [clinicInfoData, isEdit, isCopy]);

  /**
   * Period validation checks.
   * - Create: available_from must be after the available_to of the record with the largest sub_number.
   * - Edit: check adjacent records (prev/next sub_number) for overlap.
   */
  const runPeriodChecks = (fromStr: string, toStr: string | null | undefined): boolean => {
    let hasError = false;

    if (!isEdit) {
      // Create mode: check against latest sub_number record
      const latestAvailableTo = latestAvailableToGetterRef.current?.();
      if (fromStr && latestAvailableTo && fromStr <= latestAvailableTo) {
        methods.setError('clinicInfo.available_from', {
          type: 'manual',
          message: '既存治療院情報の適用期間より前の日付は設定できません。',
        });
        hasError = true;
      }
    } else {
      // Edit mode: check against adjacent records (prev/next sub_number)
      const subNumber = methods.getValues('clinicInfo.sub_number');
      if (subNumber != null && adjacentPeriodCheckerRef.current) {
        const result = adjacentPeriodCheckerRef.current(subNumber, fromStr, toStr);
        if (result.fromError) {
          methods.setError('clinicInfo.available_from', {
            type: 'manual',
            message: result.fromError,
          });
          hasError = true;
        }
        if (result.toError) {
          methods.setError('clinicInfo.available_to', {
            type: 'manual',
            message: result.toError,
          });
          hasError = true;
        }
      }
    }

    return hasError;
  };

  const handleFormSubmit = async (data: ClinicCreateFormData) => {
    if (!onSubmit) return;

    const fromStr = data.clinicInfo.available_from as string | null | undefined;
    const toStr = data.clinicInfo.available_to as string | null | undefined;
    if (fromStr && runPeriodChecks(fromStr, toStr)) {
      return;
    }

    const submissionData = {
      ...data,
      clinicInfo: {
        ...data.clinicInfo,
        name_kana: data.clinicInfo.name_kana.trim() === '' ? ' ' : data.clinicInfo.name_kana,
      },
    };

    try {
      await onSubmit(submissionData);
    } catch (error) {
      applyClinicInformationApiValidationErrors(methods.setError, error);
    }
  };

  /** Runs period checks even when Zod validation fails, surfacing overlap errors on every submit. */
  const handleInvalidFormSubmit = () => {
    const clinicInfo = methods.getValues('clinicInfo');
    const fromStr = toDateStr(clinicInfo.available_from);
    const toStr = toDateStr(clinicInfo.available_to);
    if (fromStr) runPeriodChecks(fromStr, toStr);
    window.scrollTo(0, 0);
  };

  return (
    <FormProvider {...methods}>
      <AppContainer>
        <div className="mb-4">
          <AppBreadcrumb items={breadcrumbItems} paramsLabel="編集" />
        </div>
        <AppCard title={isEdit ? `治療院情報編集: ${id ?? ''}` : '治療院情報作成'}>
          <form onSubmit={methods.handleSubmit(handleFormSubmit, handleInvalidFormSubmit)}>
            <ClinicInfoCreateForm
              clinicId={clinicId}
              isEdit={isEdit}
              isCopy={isCopy}
              onRegisterLatestAvailableToGetter={getter => {
                latestAvailableToGetterRef.current = getter;
              }}
              onRegisterAdjacentPeriodChecker={checker => {
                adjacentPeriodCheckerRef.current = checker;
              }}
            />
            <div className="flex gap-5 mt-6">
              <AppButton type="primary" size="large" htmlType="submit" loading={isLoading}>
                更新
              </AppButton>
              {clinicId && (
                <AppButton
                  size="large"
                  onClick={() =>
                    isEdit ? navigate(`/${currentSite}/admin/clinics/${clinicId}`) : navigate(-1)
                  }
                >
                  詳細画面へ戻る
                </AppButton>
              )}
            </div>
          </form>
        </AppCard>
      </AppContainer>
    </FormProvider>
  );
};

export default SiteAdminClinicInfoFormTemplate;
